import React, { useEffect, useState, useCallback } from 'react';
import { useUserQR } from '@/hooks/user/useUserQR';
import QRCode from 'qrcode';

interface QrCodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QrCodeDialog({ isOpen, onClose }: QrCodeDialogProps): React.JSX.Element | null {
  const {
    isLoading,
    error,
    currentQrCode,
    getQrCode,
    refreshQrCode,
    clearError
  } = useUserQR();

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [qrImageData, setQrImageData] = useState<string | null>(null);
  const [generatingQr, setGeneratingQr] = useState(false);

  // 生成二维码图片
  const generateQrImage = useCallback(async (qrId: string) => {
    if (!qrId) return;

    setGeneratingQr(true);
    try {
      // 使用二维码ID生成图片数据
      const qrDataURL = await QRCode.toDataURL(qrId, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrImageData(qrDataURL);
    } catch (err) {
      console.error('生成二维码图片失败:', err);
    } finally {
      setGeneratingQr(false);
    }
  }, []);

  // 当弹框打开时获取二维码
  useEffect(() => {
    if (isOpen && !currentQrCode) {
      getQrCode('user_profile');
    }
  }, [isOpen, currentQrCode, getQrCode]);

  // 当获取到二维码数据后生成图片
  useEffect(() => {
    if (currentQrCode?.id) {
      generateQrImage(currentQrCode.id);
    }
  }, [currentQrCode, generateQrImage]);

  // 关闭弹框时清理状态
  useEffect(() => {
    if (!isOpen) {
      clearError();
      setQrImageData(null);
    }
  }, [isOpen, clearError]);

  // 刷新二维码
  const handleRefresh = async () => {
    setQrImageData(null);
    const newQrCode = await refreshQrCode('user_profile');
    if (newQrCode?.id) {
      await generateQrImage(newQrCode.id);
    }
  };

  // 下载二维码图片
  const handleDownload = async () => {
    if (!qrImageData) return;

    setDownloadLoading(true);
    try {
      // 创建一个临时的a标签来下载图片
      const link = document.createElement('a');
      link.href = qrImageData;
      link.download = `my_qr_${new Date().getTime()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('下载二维码失败:', err);
    } finally {
      setDownloadLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* 标题 */}
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">我的二维码</h3>
          <p className="text-sm text-gray-500 mt-1">扫描二维码添加我为好友</p>
        </div>

        {/* 内容区域 */}
        <div className="text-center">
          {(isLoading || generatingQr) ? (
            // 加载状态
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-500">
                {isLoading ? '正在获取二维码数据...' : '正在生成二维码图片...'}
              </p>
            </div>
          ) : error ? (
            // 错误状态
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-red-500 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                重试
              </button>
            </div>
          ) : currentQrCode && qrImageData ? (
            // 二维码显示
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg border-2 border-gray-100 mb-6">
                <img
                  src={qrImageData}
                  alt="我的二维码"
                  className="w-48 h-48 object-contain"
                />
              </div>

              {/* 二维码ID显示 */}
              <div className="text-xs text-gray-400 mb-6 text-center">
                <p>二维码ID: {currentQrCode.id}</p>
                {currentQrCode.type && (
                  <p className="mt-1">类型: {currentQrCode.type}</p>
                )}
              </div>

              {/* 操作按钮 */}
              <div className="flex space-x-3 w-full">
                <button
                  onClick={handleRefresh}
                  disabled={isLoading || generatingQr}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading || generatingQr ? '刷新中...' : '刷新'}
                </button>
                <button
                  onClick={handleDownload}
                  disabled={downloadLoading || !qrImageData}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadLoading ? '下载中...' : '下载'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
